import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useHome } from "../api/queries";
import { waitForBannerImages, waitForFirstBannerImage } from "../lib/banners";
import {
  bannerShell,
  bannerFrame,
  bannerImage,
  bannerGradient,
  bannerTitleWrap,
  bannerTitleInner,
  bannerTitle,
} from "../lib/banner";

const AUTOPLAY_MS = 6000;

function HeroSkeleton() {
  return (
    <section className={`${bannerShell} ${bannerFrame} flex items-center justify-center`}>
      <div className="absolute inset-0 bg-neutral-800 animate-pulse" aria-hidden="true" />
      <div className="relative text-slate-400 text-sm font-semibold tracking-wider uppercase animate-pulse">
        Loading banners...
      </div>
    </section>
  );
}

export default function Hero() {
  const { data, isLoading, error, refetch, isFetching } = useHome();
  const slides = data?.banners ?? [];
  const slideImages = useMemo(
    () => slides.map((slide) => slide.image).filter(Boolean),
    [slides]
  );
  const [active, setActive] = useState(0);
  const [imagesReady, setImagesReady] = useState(false);

  useEffect(() => {
    setActive(0);
    setImagesReady(false);
  }, [slideImages.join("|")]);

  useEffect(() => {
    if (slideImages.length === 0) {
      setImagesReady(false);
      return undefined;
    }

    let cancelled = false;

    waitForFirstBannerImage(slideImages).then(() => {
      if (cancelled) return;
      setImagesReady(true);
      waitForBannerImages(slideImages);
    });

    return () => {
      cancelled = true;
    };
  }, [slideImages]);

  useEffect(() => {
    if (!imagesReady || slides.length <= 1) return undefined;

    const t = window.setInterval(() => {
      setActive((prev) => (prev + 1) % slides.length);
    }, AUTOPLAY_MS);

    return () => window.clearInterval(t);
  }, [imagesReady, slides.length]);

  if (isLoading || (slides.length > 0 && !imagesReady)) {
    return <HeroSkeleton />;
  }

  if (error || slides.length === 0) {
    return (
      <section className={`${bannerShell} ${bannerFrame} flex items-center justify-center px-6`}>
        <div className="text-center max-w-md">
          <p className="text-slate-600">{error?.message || "No home banners available."}</p>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="mt-4 text-sm font-bold text-[#aad4fa] hover:text-white transition-colors disabled:opacity-50"
          >
            Retry
          </button>
        </div>
      </section>
    );
  }

  const slide = slides[active];
  const canNavigate = slides.length > 1;

  const goPrev = () => setActive((p) => (p - 1 + slides.length) % slides.length);
  const goNext = () => setActive((p) => (p + 1) % slides.length);

  return (
    <section className={bannerShell}>
      <div className={bannerFrame}>
        {slides.map((s, i) => (
          <img
            key={s.id}
            src={s.image}
            alt=""
            className={`${bannerImage} transition-opacity duration-700 ${
              i === active ? "opacity-100" : "opacity-0"
            }`}
            fetchPriority={i === 0 ? "high" : "low"}
            loading={i === 0 ? "eager" : "lazy"}
            decoding="async"
            aria-hidden={i !== active}
          />
        ))}

        <div className={bannerGradient} aria-hidden="true" />

        {canNavigate && (
          <>
            <button
              type="button"
              onClick={goPrev}
              className="absolute left-2 sm:left-3 md:left-6 top-1/2 z-20 -translate-y-1/2 flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-full bg-black/35 text-white backdrop-blur-sm transition-colors hover:bg-black/50 md:bg-black/25 md:hover:bg-black/40"
              aria-label="Previous slide"
            >
              <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
            <button
              type="button"
              onClick={goNext}
              className="absolute right-2 sm:right-3 md:right-6 top-1/2 z-20 -translate-y-1/2 flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-full bg-black/35 text-white backdrop-blur-sm transition-colors hover:bg-black/50 md:bg-black/25 md:hover:bg-black/40"
              aria-label="Next slide"
            >
              <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </>
        )}

        <div className={bannerTitleWrap}>
          <div className={bannerTitleInner}>
            <h3 key={`title-${slide.id}`} className={bannerTitle}>
              {slide.title}
            </h3>
          </div>
        </div>

        {canNavigate && (
          <div className="absolute bottom-4 left-1/2 z-20 flex -translate-x-1/2 gap-1.5 md:bottom-8 md:left-auto md:right-10 md:translate-x-0">
            {slides.map((s, i) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setActive(i)}
                className={`h-1 rounded-full transition-all duration-300 ${
                  i === active
                    ? "w-8 bg-[#1e6fcf]"
                    : "w-4 bg-white/40 hover:bg-white/60 md:bg-black/25 md:hover:bg-black/40"
                }`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
